import { DatePipe, NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccordionModule } from 'primeng/accordion';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { take } from 'rxjs';
import { CignaIconComponent } from '../../../../../shared/components/cigna-icon/cigna-icon.component';
import { StorageKeys } from '../../../../../shared/enums/storage-keys.enum';
import { FileAttachFieldComponent } from '../../../../../shared/form-fields/file-attach-field/file-attach-field.component';
import { AttachedFile } from '../../../../../shared/models/attached-file';
import { InsuredData, UserData } from '../../../../../shared/models/user-data';
import { MessageService } from '../../../../../shared/services/message.service';
import { StorageService } from '../../../../../shared/services/storage.service';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { RegisterData } from '../../../../public/register/models/register';
import { Documentos } from '../../models/mi-cigna';
import { MiCignaService } from '../../services/mi-cigna.service';

@Component({
  selector: 'app-sign-document',
  templateUrl: './sign-document.component.html',
  standalone: true,
  imports: [
    AccordionModule,
    ButtonModule,
    CignaIconComponent,
    DatePipe,
    FileAttachFieldComponent,
    FormsModule,
    InputSwitchModule,
    NgClass,
    ReactiveFormsModule,
    SharedModule,
    TranslateModule,
  ],
})
export class SignDocumentComponent implements OnInit {
  private _dynamicDialogConfig = inject(DynamicDialogConfig);
  private _dynamicDialogRef = inject(DynamicDialogRef);
  private _messageService = inject(MessageService);
  private _miCignaService = inject(MiCignaService);
  private _storageService = inject(StorageService);
  private _translateService = inject(TranslateService);
  private _utilsService = inject(UtilsService);

  checked: boolean = false;
  checkedAccept: boolean = false;

  private registerData!: RegisterData;

  documentId: number = 0;
  documentType: string = '';
  documentName: string = '';
  generationDate: string = '';
  signature: string = '';
  signatureStatus: string = '';
  activeTabIndex: number | null = 0;
  secondActiveTabIndex: number | null = null;
  terms: boolean = false;
  termsAccept: boolean = false;
  activeTab: number = 0;
  protected file: string = '';
  protected selectedFile: File | null = null;
  attachedFiles: AttachedFile[] = [];
  readonly fileAttachAccept = '.pdf';
  logData!: InsuredData;
  showError: boolean = false;
  showErrorAccept: boolean = false;
  showFileError: boolean = false;
  userData!: UserData;

  isSimulating: boolean =
    this._utilsService.isSimulating() && this._dynamicDialogConfig.data?.documentId;

  selectedDocument: Documentos | null = null;
  newStatus: string = '';

  signNeeded: boolean = false;
  acceptanceNeeded: boolean = false;
  sentToSign: string = '';

  ngOnInit(): void {
    if (this._dynamicDialogConfig.data?.documentId) {
      this.documentId = this._dynamicDialogConfig.data?.documentId;
      this.documentType = this._dynamicDialogConfig.data?.documentType;
      this.generationDate = this._dynamicDialogConfig.data?.generationDate;
      this.signature = this._dynamicDialogConfig.data?.signature;
      this.signatureStatus = this._dynamicDialogConfig.data?.signatureStatus;
      this.documentName = this._dynamicDialogConfig.data?.documentName;
    } else if (this._dynamicDialogConfig.data?.document) {
      this.selectedDocument = this._dynamicDialogConfig.data?.document;
      if (this.selectedDocument) {
        this.documentId = this.selectedDocument.id;
        this.documentType = this.selectedDocument.name;
        this.generationDate = this.selectedDocument.generationDate;
        this.signature = this._dynamicDialogConfig.data?.signature;
        this.signatureStatus = this.selectedDocument.signatureStatus;
        this.documentName = this.selectedDocument.name;
        this.signNeeded = this.selectedDocument.signNeeded;
        this.acceptanceNeeded = this.selectedDocument.acceptanceNeeded;
        this.sentToSign = this.selectedDocument.sentToSign;
      }
    }

    this.userData =
      this._storageService.sessionStorageGetItem(StorageKeys.SELECTED_AUTHORIZED) ||
      this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);

    this.terms = false;
    this.checked = false;
  }

  showTerms(type: string): void {
    if (type == 'ACCEPT') {
      this.termsAccept = true;
      this.checkedAccept = false;
      this.showErrorAccept = false;
    } else {
      this.terms = true;
      this.checked = false;
      this.showError = false;
    }

    this._miCignaService
      .getSignTerms()
      .pipe(take(1))
      .subscribe((data: any) => {
        if (data.type.indexOf('json') > 0 || data.type.indexOf('xml') > 0) {
          this._messageService.showMessage('error', 'back_errors.download_document');
        } else {
          const fileName = 'condiciones-firma-digital.pdf';
          this._utilsService.saveFile(data, fileName);
        }
      });
  }

  aceptar(): void {
    this.showErrorAccept = false;
    if (!this.termsAccept || !this.checkedAccept) {
      this.showErrorAccept = true;
    } else {
      this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
      this._miCignaService
        .postAcceptDocument(this.userData.insuranceNumber, this.documentId)
        .pipe(take(1))
        .subscribe(resp => {
          if (!!resp.functionalExceptions && resp['functionalExceptions'].length > 0) {
            this._messageService.showMessage(
              'error',
              `Error: ${resp['functionalExceptions'][0].message}`
            );
          } else {
            this._messageService.showMessage('success', 'sign.accept.success');
            if (this.selectedDocument) {
              this.selectedDocument.signatureStatus =
                this._translateService.instant('sign.status.accepted');
              this._dynamicDialogRef.close(this.selectedDocument.signatureStatus);
            }
          }
        });
    }
  }

  firmar(): void {
    this.showError = false;
    this.showFileError = false;
    if (!this.terms || !this.checked) {
      this.showError = true;
    } else if (!this.selectedFile) {
      this.showFileError = true;
    } else {
      this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);

      const acceptDocumentResquestBean = {
        insuranceNumber: this.userData.insuranceNumber,
        documentId: this.documentId,
      };

      const formData: FormData = new FormData();
      const requestBlob: Blob = new Blob([JSON.stringify(acceptDocumentResquestBean)], {
        type: 'application/json',
      });

      formData.append('request', requestBlob);
      if (this.selectedFile) {
        formData.append('file', this.selectedFile, this.file);
      }
      this._miCignaService.postSignDocument(formData).subscribe(resp => {
        if (!!resp.functionalExceptions && resp['functionalExceptions'].length > 0) {
          this._messageService.showMessage(
            'error',
            `Error: ${resp['functionalExceptions'][0].message}`
          );
        } else {
          this._messageService.showMessage('success', 'sign.accept.success');
          if (this.selectedDocument) {
            this.selectedDocument.signatureStatus = this._translateService.instant(
              'sign.status.signedOnline'
            );
            this._dynamicDialogRef.close(this.selectedDocument.signatureStatus);
          }
        }
      });
    }
  }

  firmarPostal(): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);

    const acceptDocumentResquestBean = {
      insuranceNumber: this.userData.insuranceNumber,
      documentId: this.documentId,
    };

    const formData: FormData = new FormData();
    const requestBlob: Blob = new Blob([JSON.stringify(acceptDocumentResquestBean)], {
      type: 'application/json',
    });

    formData.append('request', requestBlob);
    this._miCignaService.postSignDocumentPostal(formData).subscribe(resp => {
      if (!!resp.functionalExceptions && resp['functionalExceptions'].length > 0) {
        this._messageService.showMessage(
          'error',
          `Error: ${resp['functionalExceptions'][0].message}`
        );
      } else {
        this._messageService.showMessage('success', 'sign.accept.success');
        if (this.selectedDocument) {
          this.selectedDocument.signatureStatus =
            this._translateService.instant('sign.status.signedPost');
          this._dynamicDialogRef.close(this.selectedDocument.signatureStatus);
        }
      }
    });
  }

  cambiar(): void {
    this.checked = !this.checked;
  }

  switchTab(tab: number): void {
    this.activeTab = tab;
    this.deleteFile();
  }

  onFilesReceived(files: File[]): void {
    if (!files.length) {
      return;
    }
    this.setSelectedFile(files[0]);
  }

  onFilesRejected(): void {
    this.showFileError = true;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const uploadedFile = input.files?.[0];
    if (uploadedFile) {
      this.setSelectedFile(uploadedFile);
      input.value = '';
    }
  }

  deleteFile(): void {
    this.selectedFile = null;
    this.file = '';
    this.attachedFiles = [];
  }

  private setSelectedFile(file: File): void {
    this.selectedFile = file;
    this.file = file.name;
    this.showFileError = false;
    this.attachedFiles = [{ name: file.name, size: file.size }];
  }

  obtenerFirma(field: string): string {
    let firmaitem: string = this._translateService.instant(
      'mi_cigna.mis_documentos.pendiente_firma'
    );
    if (field != null && field != '') {
      firmaitem = field;
    }

    return firmaitem;
  }

  downloadFile(documentId: number): void {
    this.logData = this._storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    this._miCignaService
      .downloadFile(documentId, this.userData.insuranceNumber)
      .pipe(take(1))
      .subscribe((data: any) => {
        if (data.type.indexOf('json') > 0 || data.type.indexOf('xml') > 0) {
          this._messageService.showMessage('error', 'back_errors.download_document');
        } else {
          this._utilsService.saveFile(data, this.documentName);
        }
      });
  }

  fileSize(size: number): string {
    return this._utilsService.fileSize(size);
  }

  onAccordionOpen(index: number): void {
    if (index === 1) {
      this.activeTabIndex = 0;
      this.secondActiveTabIndex = 1;
    } else if (index === 2) {
      this.secondActiveTabIndex = 0;
      this.activeTabIndex = 1;
    }
  }
}
